import { createListenerMiddleware } from '@reduxjs/toolkit';

import { detailedPricingReducer, fetchDetailedPricing } from './detailedPricingSlice';
import {
    applyDiscountCode, discountCodeSet, discountReducer, selectDiscountCode,
} from './discountSlice';
import { orderIdReceived, ordersReducer } from './ordersSlice';

import type { TypedStartListening } from '@reduxjs/toolkit';

export interface OrdersListenerOptions {
    /** Called instead of console.error when an error occurs in the listener.
     *  Falls back to console.error when not provided. */
    onError?: (context: string, error: unknown, meta: Record<string, unknown>) => void;
}

/**
 * The listener middleware reads state via default slice selectors, which
 * assume these exact keys. If the host app mounts slices under different
 * keys, use getDiscountSelectors / getDetailedPricingSelectors /
 * getOrdersSelectors instead, and do NOT use ordersListenerMiddleware.
 */
export interface OrdersRootState {
    detailedPricing: ReturnType<typeof detailedPricingReducer>;
    discount: ReturnType<typeof discountReducer>;
    orders: ReturnType<typeof ordersReducer>;
}

function defaultOnError(context: string, error: unknown, meta: Record<string, unknown>): void {
    // eslint-disable-next-line no-console -- standalone package; default fallback when host app provides no onError
    console.error(context, error, meta);
}

export function createOrdersListenerMiddleware(options?: OrdersListenerOptions) {
    const logError = options?.onError ?? defaultOnError;
    const middleware = createListenerMiddleware();

    const startAppListening = middleware.startListening as
        TypedStartListening<OrdersRootState>;

    startAppListening({
        actionCreator: orderIdReceived,
        effect: async (action, listenerApi) => {
            // Cancels the *effect* of any previous listener invocation, but does
            // NOT abort thunks already dispatched by it. If two orderIdReceived
            // fire in quick succession, both API calls may complete — the
            // staleness guards in both detailedPricingSlice and discountSlice
            // (requestedOrderId) ensure the most-recently-requested order wins
            // regardless of resolution order.
            listenerApi.cancelActiveListeners();
            const orderId = action.payload.orderId;
            // Discount code priority: (1) Redux state (set via discountCodeSet or a
            // previous URL param lookup), (2) URL ?discountCode= param (client-side
            // only; skipped during SSR). When found, the URL param is dispatched via
            // discountCodeSet so subsequent listeners see it without re-reading the URL.
            let discountCode = selectDiscountCode(listenerApi.getState());
            if (!discountCode && typeof window !== 'undefined') {
                const urlCode = new URLSearchParams(window.location.search).get('discountCode');
                if (urlCode) {
                    listenerApi.dispatch(discountCodeSet(urlCode));
                    discountCode = urlCode;
                }
            }

            if (discountCode) {
                const discountResult = await listenerApi.dispatch(
                    applyDiscountCode({ discountCode, orderId }),
                );
                if (applyDiscountCode.rejected.match(discountResult)) {
                    // Discount application failed — discountSlice's rejected handler
                    // stores the error in state.errorCode. Consumers should read this
                    // via selectDiscountErrorCode and render DiscountError to show it.
                    // Pricing is fetched regardless so the user still sees their order total.
                    logError('[wanderu-orders] Failed to apply discount code:', discountResult.error, {
                        discountCode,
                        orderId,
                        payload: discountResult.payload,
                    });
                }
                // If a newer orderIdReceived cancelled this listener while we were
                // awaiting the discount call, stop here — the newer listener handles
                // pricing. Without this check, the cancelled listener would proceed
                // to fetchDetailedPricing and clobber the newer listener's state.
                if (listenerApi.signal.aborted) return;
            }

            const pricingResult = await listenerApi.dispatch(fetchDetailedPricing(orderId));
            if (fetchDetailedPricing.rejected.match(pricingResult)) {
                logError('[wanderu-orders] Failed to fetch detailed pricing:', pricingResult.error, {
                    orderId,
                    payload: pricingResult.payload,
                });
            }
        },
    });

    return middleware;
}

/** Default instance for apps that mount slices at the standard keys.
 *  Uses console.error for error logging; pass onError to
 *  createOrdersListenerMiddleware for custom handling. */
export const ordersListenerMiddleware = createOrdersListenerMiddleware();
